import { useEffect } from 'react';
import { readData } from '@/firebase/database';

export interface ThemeSettings {
  neonCyan: string;
  neonBlue: string;
  neonGreen: string;
  neonPurple: string;
  neonPink: string;
  background: string;
  foreground: string;
  primary: string;
}

export const themePresets: Record<string, Partial<ThemeSettings>> = {
  'Metallic Silver': { neonCyan: '0 0% 80%', neonBlue: '220 10% 55%', primary: '0 0% 75%', background: '0 0% 3%', foreground: '0 0% 88%' },
  'Warm Gold': { neonCyan: '40 60% 55%', neonBlue: '30 50% 50%', primary: '40 60% 55%', background: '30 10% 3%', foreground: '40 15% 88%' },
  'Cool Steel': { neonCyan: '210 15% 60%', neonBlue: '220 20% 50%', primary: '210 15% 60%', background: '220 10% 4%', foreground: '210 10% 85%' },
  'Cyber Cyan': { neonCyan: '185 100% 48%', neonBlue: '210 100% 58%', primary: '185 100% 48%', background: '224 25% 2%', foreground: '210 30% 90%' },
  'Pure Mono': { neonCyan: '0 0% 65%', neonBlue: '0 0% 45%', primary: '0 0% 65%', background: '0 0% 2%', foreground: '0 0% 90%' },
  'Rose Gold': { neonCyan: '350 40% 65%', neonBlue: '340 30% 50%', primary: '350 40% 65%', background: '350 8% 3%', foreground: '350 10% 88%' },
  'Emerald': { neonCyan: '160 50% 45%', neonBlue: '140 40% 40%', primary: '160 50% 45%', background: '160 15% 3%', foreground: '160 10% 88%' },
  'Bronze': { neonCyan: '25 50% 50%', neonBlue: '20 40% 45%', primary: '25 50% 50%', background: '25 12% 3%', foreground: '25 10% 88%' },
  'Platinum': { neonCyan: '220 5% 72%', neonBlue: '225 8% 55%', primary: '220 5% 72%', background: '220 5% 4%', foreground: '220 5% 90%' },
  'Copper': { neonCyan: '15 60% 50%', neonBlue: '10 50% 45%', primary: '15 60% 50%', background: '15 12% 3%', foreground: '15 8% 88%' },
  'Titanium Blue': { neonCyan: '220 60% 55%', neonBlue: '230 50% 48%', primary: '220 60% 55%', background: '225 15% 3%', foreground: '220 10% 88%' },
  'Obsidian': { neonCyan: '270 30% 50%', neonBlue: '280 25% 42%', primary: '270 30% 50%', background: '270 10% 2%', foreground: '270 5% 88%' },
  'Champagne': { neonCyan: '45 40% 60%', neonBlue: '40 35% 50%', primary: '45 40% 60%', background: '45 8% 3%', foreground: '45 10% 90%' },
  'Arctic Ice': { neonCyan: '195 50% 55%', neonBlue: '200 40% 48%', primary: '195 50% 55%', background: '200 12% 3%', foreground: '195 8% 90%' },
  'Volcanic Red': { neonCyan: '5 60% 48%', neonBlue: '0 50% 42%', primary: '5 60% 48%', background: '5 12% 3%', foreground: '0 8% 88%' },
  'Midnight Indigo': { neonCyan: '240 45% 55%', neonBlue: '245 40% 48%', primary: '240 45% 55%', background: '240 15% 3%', foreground: '240 8% 90%' },
};

// Derive light-mode equivalents from a dark-mode theme
const deriveLightValues = (theme: Partial<ThemeSettings>): Record<string, string> => {
  const light: Record<string, string> = {};

  // For accent/neon colors, keep hue & saturation but adjust lightness for light backgrounds
  if (theme.neonCyan) {
    const parts = theme.neonCyan.match(/[\d.]+/g);
    if (parts && parts.length >= 3) {
      const l = parseFloat(parts[2]);
      light.neonCyan = `${parts[0]} ${parts[1]}% ${Math.max(15, Math.min(35, 100 - l))}%`;
    }
  }
  if (theme.neonBlue) {
    const parts = theme.neonBlue.match(/[\d.]+/g);
    if (parts && parts.length >= 3) {
      const l = parseFloat(parts[2]);
      light.neonBlue = `${parts[0]} ${parts[1]}% ${Math.max(20, Math.min(40, 100 - l))}%`;
    }
  }
  if (theme.primary) {
    const parts = theme.primary.match(/[\d.]+/g);
    if (parts && parts.length >= 3) {
      const l = parseFloat(parts[2]);
      light.primary = `${parts[0]} ${parts[1]}% ${Math.max(15, Math.min(30, 100 - l))}%`;
    }
  }

  return light;
};

const isLightMode = () => window.matchMedia('(prefers-color-scheme: light)').matches;

export const applyTheme = (theme: Partial<ThemeSettings>) => {
  const root = document.documentElement;
  const light = isLightMode();

  // In light mode, only apply accent colors (not bg/fg), and invert them for readability
  if (light) {
    const lightVals = deriveLightValues(theme);

    if (lightVals.neonCyan) {
      root.style.setProperty('--neon-cyan', lightVals.neonCyan);
    }
    if (lightVals.neonBlue) {
      root.style.setProperty('--neon-blue', lightVals.neonBlue);
    }
    if (lightVals.primary) {
      root.style.setProperty('--primary', lightVals.primary);
      root.style.setProperty('--accent', lightVals.primary);
      root.style.setProperty('--ring', lightVals.primary);
      root.style.setProperty('--sidebar-primary', lightVals.primary);
      root.style.setProperty('--sidebar-ring', lightVals.primary);
    }
    // Do NOT set background, foreground, card, muted, etc in light mode
    // Those are handled by the CSS @media (prefers-color-scheme: light) block
    if (theme.neonGreen) {
      const parts = theme.neonGreen.match(/[\d.]+/g);
      if (parts && parts.length >= 3) {
        root.style.setProperty('--neon-green', `${parts[0]} ${parts[1]}% ${Math.max(20, Math.min(35, 100 - parseFloat(parts[2])))}%`);
      }
    }
    if (theme.neonPurple) {
      const parts = theme.neonPurple.match(/[\d.]+/g);
      if (parts && parts.length >= 3) {
        root.style.setProperty('--neon-purple', `${parts[0]} ${parts[1]}% ${Math.max(20, Math.min(38, 100 - parseFloat(parts[2])))}%`);
      }
    }
    if (theme.neonPink) {
      const parts = theme.neonPink.match(/[\d.]+/g);
      if (parts && parts.length >= 3) {
        root.style.setProperty('--neon-pink', `${parts[0]} ${parts[1]}% ${Math.max(20, Math.min(38, 100 - parseFloat(parts[2])))}%`);
      }
    }
    return;
  }

  // Dark mode: apply everything as before
  if (theme.neonCyan) root.style.setProperty('--neon-cyan', theme.neonCyan);
  if (theme.neonBlue) root.style.setProperty('--neon-blue', theme.neonBlue);
  if (theme.neonGreen) root.style.setProperty('--neon-green', theme.neonGreen);
  if (theme.neonPurple) root.style.setProperty('--neon-purple', theme.neonPurple);
  if (theme.neonPink) root.style.setProperty('--neon-pink', theme.neonPink);
  if (theme.background) {
    root.style.setProperty('--background', theme.background);
    root.style.setProperty('--sidebar-background', theme.background);
    const [h, s] = theme.background.split(' ');
    root.style.setProperty('--card', `${h} ${s} 5%`);
    root.style.setProperty('--muted', `${h} ${s} 7%`);
    root.style.setProperty('--secondary', `${h} ${s} 8%`);
    root.style.setProperty('--border', `${h} ${s} 12%`);
    root.style.setProperty('--input', `${h} ${s} 7%`);
    root.style.setProperty('--glass-bg', `${h} ${s} 5%`);
    root.style.setProperty('--glass-border', `${h} ${s} 14%`);
    root.style.setProperty('--primary-foreground', theme.background);
    root.style.setProperty('--accent-foreground', theme.background);
    root.style.setProperty('--sidebar-accent', `${h} ${s} 7%`);
    root.style.setProperty('--sidebar-border', `${h} ${s} 12%`);
  }
  if (theme.foreground) {
    root.style.setProperty('--foreground', theme.foreground);
    root.style.setProperty('--card-foreground', theme.foreground);
    root.style.setProperty('--sidebar-foreground', theme.foreground);
  }
  if (theme.primary) {
    root.style.setProperty('--primary', theme.primary);
    root.style.setProperty('--accent', theme.primary);
    root.style.setProperty('--ring', theme.primary);
    root.style.setProperty('--sidebar-primary', theme.primary);
    root.style.setProperty('--sidebar-ring', theme.primary);
    root.style.setProperty('--neon-cyan', theme.primary);
  }
};

// Clear all inline theme overrides (used when switching to light mode)
const clearInlineTheme = () => {
  const root = document.documentElement;
  const props = [
    '--background', '--foreground', '--card', '--card-foreground',
    '--popover', '--popover-foreground', '--primary', '--primary-foreground',
    '--secondary', '--secondary-foreground', '--muted', '--muted-foreground',
    '--accent', '--accent-foreground', '--border', '--input', '--ring',
    '--neon-cyan', '--neon-blue', '--neon-green', '--neon-purple', '--neon-pink',
    '--glass-bg', '--glass-border',
    '--sidebar-background', '--sidebar-foreground', '--sidebar-primary',
    '--sidebar-primary-foreground', '--sidebar-accent', '--sidebar-accent-foreground',
    '--sidebar-border', '--sidebar-ring',
  ];
  props.forEach(p => root.style.removeProperty(p));
};

const ThemeProvider = () => {
  useEffect(() => {
    let interval: ReturnType<typeof setInterval> | null = null;
    let currentTheme: Partial<ThemeSettings> | null = null;

    const applyCurrentTheme = () => {
      if (currentTheme) {
        clearInlineTheme();
        applyTheme(currentTheme);
      }
    };

    // Listen for OS color scheme changes
    const mediaQuery = window.matchMedia('(prefers-color-scheme: light)');
    const handleSchemeChange = () => applyCurrentTheme();
    mediaQuery.addEventListener('change', handleSchemeChange);

    readData('theme').then((theme) => {
      if (!theme) return;

      if (theme.randomOnLoad) {
        const presetNames = Object.keys(themePresets);
        const randomPreset = themePresets[presetNames[Math.floor(Math.random() * presetNames.length)]];
        currentTheme = randomPreset;
        applyTheme(randomPreset);
      } else {
        currentTheme = theme;
        applyTheme(theme);
      }

      if (theme.autoSwitch) {
        const presetNames = Object.keys(themePresets);
        let idx = 0;
        interval = setInterval(() => {
          idx = (idx + 1) % presetNames.length;
          currentTheme = themePresets[presetNames[idx]];
          applyTheme(themePresets[presetNames[idx]]);
        }, 10000);
      }
    });

    return () => {
      mediaQuery.removeEventListener('change', handleSchemeChange);
      if (interval) clearInterval(interval);
    };
  }, []);

  return null;
};

export default ThemeProvider;
